import { useState, useEffect } from 'react'
import { FreetextSuggestion } from '@/types/admin'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Label } from '@brent/core-ui/ui'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import { Card, CardContent } from '@brent/core-ui/ui'
import { AlertCircle, Check, Link2 } from 'lucide-react'

interface MergeFreetextDialogProps {
  suggestions: [FreetextSuggestion, FreetextSuggestion] | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onMerge: (
    masterId: string,
    duplicateId: string,
    mergeComposer: boolean,
    mergeWork: boolean,
  ) => void
  isMerging: boolean
}

type MergeType = 'piece' | 'composer' | 'manual'

export function MergeFreetextDialog({
  suggestions,
  open,
  onOpenChange,
  onMerge,
  isMerging,
}: MergeFreetextDialogProps) {
  const [selectedMasterId, setSelectedMasterId] = useState<string | null>(null)
  const [mergeType, setMergeType] = useState<MergeType>('manual')

  // Detect merge type and auto-select master based on Wikidata IDs
  useEffect(() => {
    if (!suggestions) {
      setSelectedMasterId(null)
      setMergeType('manual')
      return
    }

    const [first, second] = suggestions

    // Check composer match
    const composerMatch =
      first.composer.toLowerCase().trim() === second.composer.toLowerCase().trim() ||
      (first.composer_wikidata_id &&
        second.composer_wikidata_id &&
        first.composer_wikidata_id === second.composer_wikidata_id)

    // Check work match
    const workMatch =
      first.title.toLowerCase().trim() === second.title.toLowerCase().trim() ||
      (first.work_wikidata_id &&
        second.work_wikidata_id &&
        first.work_wikidata_id === second.work_wikidata_id)

    // Determine merge type
    if (composerMatch && workMatch) {
      setMergeType('piece')
    } else if (composerMatch) {
      setMergeType('composer')
    } else {
      setMergeType('manual')
    }

    // Auto-select master based on resolved status and Wikidata ID presence
    const firstIsResolved = !!first.resolved_at
    const secondIsResolved = !!second.resolved_at
    const firstHasComposerWikidata = !!first.composer_wikidata_id
    const secondHasComposerWikidata = !!second.composer_wikidata_id
    const firstHasWorkWikidata = !!first.work_wikidata_id
    const secondHasWorkWikidata = !!second.work_wikidata_id

    // ALWAYS prefer resolved entries as master (they already have correct Wikidata IDs)
    // This takes precedence over all other selection logic
    if (firstIsResolved && !secondIsResolved) {
      setSelectedMasterId(first.suggestion_id)
    } else if (!firstIsResolved && secondIsResolved) {
      setSelectedMasterId(second.suggestion_id)
    } else if (composerMatch && workMatch) {
      // For piece merge: prefer one with both IDs, then one with work ID
      if (
        (firstHasComposerWikidata || firstHasWorkWikidata) &&
        !secondHasComposerWikidata &&
        !secondHasWorkWikidata
      ) {
        setSelectedMasterId(first.suggestion_id)
      } else if (
        !firstHasComposerWikidata &&
        !firstHasWorkWikidata &&
        (secondHasComposerWikidata || secondHasWorkWikidata)
      ) {
        setSelectedMasterId(second.suggestion_id)
      } else if (firstHasWorkWikidata && !secondHasWorkWikidata) {
        setSelectedMasterId(first.suggestion_id)
      } else if (!firstHasWorkWikidata && secondHasWorkWikidata) {
        setSelectedMasterId(second.suggestion_id)
      } else {
        setSelectedMasterId(null)
      }
    } else if (composerMatch) {
      // For composer merge: prefer one with composer Wikidata ID
      if (firstHasComposerWikidata && !secondHasComposerWikidata) {
        setSelectedMasterId(first.suggestion_id)
      } else if (!firstHasComposerWikidata && secondHasComposerWikidata) {
        setSelectedMasterId(second.suggestion_id)
      } else {
        setSelectedMasterId(null)
      }
    } else {
      // No match: prompt user to select
      setSelectedMasterId(null)
    }
  }, [suggestions])

  if (!suggestions) return null

  const [first, second] = suggestions

  const handleMerge = () => {
    if (!selectedMasterId) return

    const duplicateId =
      selectedMasterId === first.suggestion_id ? second.suggestion_id : first.suggestion_id

    // Determine which fields to merge based on merge type
    const mergeComposer = mergeType === 'piece' || mergeType === 'composer'
    const mergeWork = mergeType === 'piece'

    onMerge(selectedMasterId, duplicateId, mergeComposer, mergeWork)
  }

  const handleClose = () => {
    setSelectedMasterId(null)
    onOpenChange(false)
  }

  const renderSuggestionCard = (suggestion: FreetextSuggestion) => {
    const isSelected = selectedMasterId === suggestion.suggestion_id
    const hasComposerWikidata = !!suggestion.composer_wikidata_id
    const hasWorkWikidata = !!suggestion.work_wikidata_id

    return (
      <Card
        className={`cursor-pointer transition-all ${
          isSelected ? 'ring-2 ring-primary' : 'hover:ring-1 hover:ring-gray-300'
        }`}
        onClick={() => setSelectedMasterId(suggestion.suggestion_id)}
      >
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <Label className="text-sm font-medium">
                {isSelected ? 'Source (Canonical Data)' : 'Target (Will be Updated)'}
              </Label>
            </div>
            {isSelected && <Check className="h-5 w-5 text-primary" />}
          </div>

          <div className="space-y-2">
            <div>
              <div className="text-xs text-muted-foreground">Composer</div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{suggestion.composer}</span>
                {suggestion.is_composer_freetext && (
                  <Badge
                    variant="outline"
                    className="bg-yellow-50 text-yellow-800 border-yellow-200"
                  >
                    Freetext
                  </Badge>
                )}
                {hasComposerWikidata && (
                  <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200">
                    Wikidata
                  </Badge>
                )}
              </div>
              {suggestion.composer_wikidata_id && (
                <div className="text-xs text-muted-foreground">
                  ID: {suggestion.composer_wikidata_id}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs text-muted-foreground">Piece</div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{suggestion.title}</span>
                {suggestion.is_work_freetext && (
                  <Badge
                    variant="outline"
                    className="bg-yellow-50 text-yellow-800 border-yellow-200"
                  >
                    Freetext
                  </Badge>
                )}
                {hasWorkWikidata && (
                  <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200">
                    Wikidata
                  </Badge>
                )}
              </div>
              {suggestion.work_wikidata_id && (
                <div className="text-xs text-muted-foreground">
                  ID: {suggestion.work_wikidata_id}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs text-muted-foreground">Suggested by</div>
              <div className="text-sm">
                {suggestion.user_name || 'Unknown'} ({suggestion.user_email})
              </div>
            </div>

            {suggestion.comment && (
              <div>
                <div className="text-xs text-muted-foreground">Comment</div>
                <div className="text-sm italic text-muted-foreground">{suggestion.comment}</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    )
  }

  const hasAutoSelection = selectedMasterId !== null
  const needsManualSelection = !hasAutoSelection

  // Generate merge description
  const getMergeDescription = () => {
    if (mergeType === 'piece') {
      return 'Both suggestions refer to the same piece (composer + work match). The target will be linked to the same piece with the same Wikidata IDs.'
    } else if (mergeType === 'composer') {
      return 'Both suggestions have the same composer. The target will be linked to the same composer (Wikidata ID will be copied, works remain separate).'
    } else {
      return 'These entries do not appear to match. Please verify you selected the correct entries to merge.'
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            De-duplicate Freetext Entries
          </DialogTitle>
          <DialogDescription>
            Link these suggestions to the same canonical piece/composer by copying Wikidata IDs.
            Both suggestions will be preserved with their original users and comments.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-medium mb-1">
                {mergeType === 'piece' && 'Full Piece De-duplication'}
                {mergeType === 'composer' && 'Composer De-duplication'}
                {mergeType === 'manual' && 'Manual Selection Required'}
              </div>
              <div className="text-sm">{getMergeDescription()}</div>
            </AlertDescription>
          </Alert>

          {hasAutoSelection && (
            <Alert className="bg-blue-50 border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-900">
                The entry with Wikidata IDs has been selected as the source. Its canonical data will
                be copied to the target entry. You can change this selection if needed.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {renderSuggestionCard(first)}
            {renderSuggestionCard(second)}
          </div>

          {needsManualSelection && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please select which entry has the correct/canonical data (source). The other entry
                will be updated to match.
              </AlertDescription>
            </Alert>
          )}

          <Alert className="bg-gray-50">
            <AlertDescription className="text-sm">
              <strong>What will happen:</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                {mergeType === 'piece' && (
                  <>
                    <li>Target's composer and work Wikidata IDs will be updated to match source</li>
                    <li>Original freetext values will be preserved in history fields</li>
                  </>
                )}
                {mergeType === 'composer' && (
                  <>
                    <li>Target's composer Wikidata ID will be updated to match source</li>
                    <li>Work data remains unchanged (different pieces by same composer)</li>
                  </>
                )}
                <li>Both suggestions remain visible to their original users</li>
                <li>Comments and user data are preserved</li>
                <li>Old freetext values removed from autocomplete cache</li>
              </ul>
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isMerging}>
            Cancel
          </Button>
          <Button onClick={handleMerge} disabled={!selectedMasterId || isMerging}>
            {isMerging ? 'De-duplicating...' : 'De-duplicate Entries'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
